import apiClient from '@/api/client';

// ── File upload ───────────────────────────────────────────────────────────────

export async function getPreSignedUrl(fileName, fileType, folder = 'scheduling') {
  const { data } = await apiClient.get('/upload-url', {
    params: { fileName, fileType, folder },
  });
  return data.result;
}

export async function uploadFileToS3(presignedUrlData, file) {
  const formData = new FormData();
  Object.entries(presignedUrlData.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  const response = await apiClient.post(presignedUrlData.url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    baseURL: '', // presigned S3 URL is absolute — skip the aurora baseURL
  });
  return response;
}

export async function getImportDate() {
  const { data } = await apiClient.get('/import-date');
  return data.result[0].ImportDate;
}

// ── Allocations ───────────────────────────────────────────────────────────────

export async function getAllocations() {
  const { data } = await apiClient.get('/allocations');
  return data.result;
}

export async function getAllocationsWithDetails() {
  const { data } = await apiClient.get('/allocations/details');
  return data.result;
}

export async function getAllocationsWithClientDetails() {
  const { data } = await apiClient.get('/allocations/client-details');
  return data.result;
}

export async function getAllocationsHoursReport(management, fulltime) {
  const { data } = await apiClient.get('/allocations/hours-report', {
    params: { management, fulltime },
  });
  return data.result;
}

export async function getStartDates(management, fulltime, weeks = 52) {
  const { data } = await apiClient.get('/allocations/hours-report-dates', {
    params: { weeks, fulltime },
  });
  return data.result;
}

export async function getAllocationsFuturesHoursReport(management = false, fulltime = false, weeks = 52) {
  const { data } = await apiClient.get('/allocations/future-hours-report', {
    params: { management, fulltime, weeks },
  });
  return data.result;
}

// ── Recruitment ───────────────────────────────────────────────────────────────

export async function getRecruitment(fy) {
  const { data } = await apiClient.get('/recruitment', { params: { fy } });
  return data.result;
}

export async function importRecruitment(payload) {
  const { data } = await apiClient.post('/recruitment/import', payload);
  return data.result;
}

// ── Chargeability ─────────────────────────────────────────────────────────────

export async function getChargeabilityReport() {
  const { data } = await apiClient.get('/chargeability');
  return data.result;
}

// ── Engagements ───────────────────────────────────────────────────────────────

export async function getEngagements(weeks = 52) {
  const { data } = await apiClient.get('/engagements', { params: { weeks } });
  return data.result;
}

export async function getEngagement(id) {
  const { data } = await apiClient.get(`/engagements/${id}`);
  return data.result[0];
}

export async function getEngagementProfessionals(id) {
  const { data } = await apiClient.get(`/engagements/${id}/professionals`);
  return data.result;
}

export async function updateEngagementNotes(id, notes) {
  const { data } = await apiClient.put(`/engagements/${id}/notes`, { notes });
  return data.result;
}

export async function deleteEngagementNotes(id) {
  const { data } = await apiClient.delete(`/engagements/${id}/notes`);
  return data.result;
}

export async function updateEngagementAlias(id, alias) {
  const { data } = await apiClient.put(`/engagements/${id}/alias`, { alias });
  return data.result;
}

export async function deleteEngagementAlias(id) {
  const { data } = await apiClient.delete(`/engagements/${id}/alias`);
  return data.result;
}

// ── Professionals ─────────────────────────────────────────────────────────────

export async function getTotalWeeksAvailable() {
  const { data } = await apiClient.get('/total-weeks-available');
  return data.result;
}

export async function getProfessionals() {
  const { data } = await apiClient.get('/professionals');
  return data.result;
}

export async function getProfessional(id) {
  const { data } = await apiClient.get(`/professionals/${id}`);
  return data.result[0];
}

// Private photo: returns a short-lived presigned S3 URL, not a permanent one.
export async function getProfessionalPhotoUrl(id) {
  const { data } = await apiClient.get(`/professionals/${id}/photo`);
  return data.result.url;
}

// Small-thumbnail URLs for many professionals in one request: { [id]: url }.
export async function getProfessionalPhotoThumbUrls(ids) {
  const { data } = await apiClient.post('/professionals/photo-thumbs', { ids });
  return data.result.urls || {};
}

// Professionals present in the DB but absent from the latest Excel import.
export async function getProfessionalsMissingFromImport() {
  const { data } = await apiClient.get('/professionals/import-missing');
  return data.result;
}

export async function bulkDeleteProfessionals(ids) {
  const { data } = await apiClient.post('/professionals/bulk-delete', { ids });
  return data.result;
}

export async function getProfessionalHoursReport(id, date) {
  const { data } = await apiClient.get(`/professionals/${id}/hours-report`, {
    params: date ? { date } : undefined,
  });
  return data.result;
}

export async function getProfessionalEngagementsTotalHours(id, date) {
  const { data } = await apiClient.get(`/professionals/${id}/engagements/${date}/total-hours`);
  return data.result[0].Hours;
}

export async function updateProfessionalNotes(id, notes) {
  const { data } = await apiClient.put(`/professionals/${id}/notes`, { notes });
  return data.result;
}

export async function deleteProfessionalNotes(id) {
  const { data } = await apiClient.delete(`/professionals/${id}/notes`);
  return data.result;
}

export async function updateProfessionalSkills(id, skills) {
  const { data } = await apiClient.put(`/professionals/${id}/skills`, { skills });
  return data.result;
}

export async function deleteProfessionalSkills(id) {
  const { data } = await apiClient.delete(`/professionals/${id}/skills`);
  return data.result;
}

export async function updateProfessionalPTO(id, pto) {
  const { data } = await apiClient.put(`/professionals/${id}/pto`, { pto });
  return data.result;
}

export async function deleteProfessionalPTO(id) {
  const { data } = await apiClient.delete(`/professionals/${id}/pto`);
  return data.result;
}

export async function getProfessionalPTORanges(professionalId) {
  const { data } = await apiClient.get(`/professionals/${professionalId}/pto-ranges`);
  return data.result;
}

export async function addProfessionalPTORange(professionalId, startDate, endDate, description) {
  const { data } = await apiClient.post('/professionals/pto-ranges', {
    professionalId, startDate, endDate, description,
  });
  return data.result;
}

export async function deleteProfessionalPTORange(ptoId) {
  const { data } = await apiClient.delete(`/professionals/pto-ranges/${ptoId}`);
  return data.result;
}

export async function getProfessionalManualAllocations(professionalId) {
  const { data } = await apiClient.get(`/professionals/${professionalId}/manual-allocations`);
  return data.result;
}

export async function addProfessionalManualAllocation(professionalId, engagementName, startDate, endDate) {
  const { data } = await apiClient.post(`/professionals/${professionalId}/manual-allocations`, {
    engagementName, startDate, endDate,
  });
  return data.result;
}

export async function deleteProfessionalManualAllocation(professionalId, manualAllocationId) {
  const { data } = await apiClient.delete(`/professionals/${professionalId}/manual-allocations/${manualAllocationId}`);
  return data.result;
}

export async function deleteProfessional(id) {
  const { data } = await apiClient.delete(`/professionals/${id}`);
  return data.result;
}

export async function addProfessional(body) {
  const { data } = await apiClient.post('/professionals', body);
  return data.result;
}

export async function updateProfessional(id, professionalData) {
  const { data } = await apiClient.put(`/professionals/${id}`, professionalData);
  return data.result;
}

export async function renameProfessional(oldId, newId) {
  const { data } = await apiClient.put(`/professionals/${oldId}/rename`, { newId });
  return data.result;
}

export async function updateProfessionalStatus(id, status) {
  const { data } = await apiClient.put(`/professionals/${id}/status`, { status });
  return data.result;
}

// ── Clients ───────────────────────────────────────────────────────────────────

export async function getClients() {
  const { data } = await apiClient.get('/clients');
  return data.result;
}

export async function getClient(clientCode) {
  const { data } = await apiClient.get(`/clients/${clientCode}`);
  return data.result[0];
}

export async function getClientEngagements(clientCode) {
  const { data } = await apiClient.get(`/clients/${clientCode}/engagements`);
  return data.result;
}

export async function updateClientAlias(clientCode, alias) {
  const { data } = await apiClient.put(`/clients/${clientCode}/alias`, { alias });
  return data.result;
}

export async function deleteClientAlias(clientCode) {
  const { data } = await apiClient.delete(`/clients/${clientCode}/alias`);
  return data.result;
}

// ── Misc ──────────────────────────────────────────────────────────────────────

export async function getProcessingFileStatus() {
  const { data } = await apiClient.get('/processing-file');
  return data.result;
}

export async function getDrafts() {
  const { data } = await apiClient.get('/drafts');
  return data.result;
}

// ── Staffing Requests ────────────────────────────────────────────────────────

export async function getStaffingRequests() {
  const { data } = await apiClient.get('/staffing-requests');
  return data.result;
}

export async function addStaffingRequest(body) {
  const { data } = await apiClient.post('/staffing-requests', body);
  return data.result;
}

export async function updateStaffingRequest(id, body) {
  const { data } = await apiClient.put(`/staffing-requests/${id}`, body);
  return data.result;
}

export async function deleteStaffingRequest(id) {
  const { data } = await apiClient.delete(`/staffing-requests/${id}`);
  return data.result;
}
